import { useVideoCallState } from "@/stores/videoCallState";
import { EventManager, Events, Actions } from "@events";
import { Signaling, SignalingMessageTypes } from "./signaling";
import axios from "axios";
import { RoomActionController } from "./roomActions";
import { WebRTCRecorder } from "./recorder";
import { TextChannel } from "./textChannel";
import { useDeviceState } from "@/stores/deviceState";
import i18n from "@/i18n";
import { watch } from "vue";
import Swal from "sweetalert2";
import lifecycle from "@/libs/lifecycle-0.1.1.mjs";
import { userMediaErrorT } from "@/utils";

let roomCount = 0;
// FOR TEST
// window.TEXT_CHANNEL = null;
// window.RTC_ID = null;
class WebRTCRoom {
  constructor(rtcId, options = {}) {
    roomCount++;
    this.currentCamera = "f";
    this.localPeer = null;
    this.answered = false;
    this.errorState = false;
    this.errors = [];
    this.messages = [];
    this.isInitiator = false;
    this.iceServerUrl = null;
    this.pcConfig = null;
    this.certs = null;
    this.localStream = null;
    this.signaling = null;
    this.peerConnection = null;
    this.hasEstablishedConnection = false;
    this.axiosClient = null;
    this.callEventEmitter = null;
    this.state = null;
    this.storedCandidates = [];
    this.iceServers = [];
    this.options = options;

    this.dtlsTimeoutId = null;
    this.connectionTimeoutId = null;
    this.popupTimeoutId = null;
    this.lastResortConnectionTimeoutId = null;

    this.rtcId = rtcId;
    this.clientId = Math.random().toString(16).substring(2, 8);
    this.state = useVideoCallState(rtcId);
    this.deviceState = useDeviceState();
    this.callEventEmitter = EventManager.getEventEmitter(rtcId);
    this.localActionEmitter = EventManager.getActionEmitter(rtcId);

    function applyLogArgs() {
      let newArgs = [];
      if (roomCount > 1) {
        // last 8 characters of rtcId
        newArgs.push("%c" + rtcId.slice(-8));
        newArgs.push("color: white; font-weight: bold; background-color: purple; padding: 2px 4px; border-radius: 4px;");
      }
      for (let i = 0; i < arguments.length; i++) {
        newArgs.push(arguments[i]);
      }
      return newArgs;
    }

    this.logger = {
      log: function () {
        console.log.apply(console, applyLogArgs(...arguments));
      },
      error: function () {
        console.error.apply(console, applyLogArgs(...arguments));
      },
      warn: function () {
        console.warn.apply(console, applyLogArgs(...arguments));
      },
    };

    this.axiosClient = axios.create({
      headers: {
        RtcId: rtcId,
      },
    });

    window.webrtc_debug = this;

    this.logger.log("PureWebRTC module => WebRTCCall created", rtcId);

    if (!options.disableRecording) {
      this.webRtcRecorder = new WebRTCRecorder(this, options.recorderOptions);
    } else {
      this.webRtcRecorder = null;
      console.log("WebRTCRecorder disabled by options");
    }

    this.roomActionController = new RoomActionController(this);

    this.startRoom();

    this.candidateFilteringMode = window.CONFIG.CANDIATE_FILTERING_MODE || "ALL";

    this.logger.log("Ice candidate filtering mode: " + this.candidateFilteringMode);

    this.localActionEmitter.on(Actions.SetMediaStream, ({ media }) => {
      media.getTracks().forEach((track) => {
        console.log("Replacing track", track.kind);
        const sender = this.peerConnection.getSenders().find((s) => s.track.kind === track.kind);
        sender.replaceTrack(track);
      });

      this.state.setPeerSrcObject(this.localPeer.peerName, media);

      this.callEventEmitter.emit(Events.CameraChanged, { cameraStatus: this.currentCamera, peerName: this.localPeer.peerName });
    });

    this.localActionEmitter.on(Actions.ResetMediaStream, async () => {
      this.localActionEmitter.emit(Actions.SetMediaStream, {
        media: this.localStream,
      });
    });

    // Detecting browser close event
    // If hangup is already done from another place, do not send hangup event again
    this.isHangedUp = false;
    this.callEventEmitter.on(Events.VideoCallEnded, async ({ preventClose }) => {
      // Stop cloned extra stream tracks to prevent resource leaks
      if (this.extraStreamClonedTracks) {
        this.extraStreamClonedTracks.forEach((track) => track.stop());
        this.extraStreamClonedTracks = null;
      }

      if (preventClose) {
        return;
      }

      this.isHangedUp = true;
      this.clearPageClosingEvents();
    });

    window.addEventListener("pagehide", this.windowClosedEmitHangUp);
    window.addEventListener("beforeunload", this.windowClosedEmitHangUp);
    window.addEventListener("unload", this.windowClosedEmitHangUp);
    lifecycle.addEventListener("statechange", this.windowLifecycleCloseState);
  }

  windowClosedEmitHangUp = (event) => {
    if (this.isHangedUp) return;
    this.localActionEmitter.emit(Actions.HangUp, { hangupBy: "local" });
    this.isHangedUp = true;
    this.clearPageClosingEvents();
  };

  windowLifecycleCloseState = (event) => {
    //console.log(lifecycle.state, evt.originalEvent.type, new Date().toISOString());

    if (lifecycle.state == "terminated") {
      this.windowClosedEmitHangUp(event);

      //console.log("terminated");
    }
  };

  clearPageClosingEvents() {
    // Clear closing page events
    window.removeEventListener("pagehide", this.windowClosedEmitHangUp);
    window.removeEventListener("beforeunload", this.windowClosedEmitHangUp);
    window.removeEventListener("unload", this.windowClosedEmitHangUp);
    lifecycle.removeEventListener("statechange", this.windowLifecycleCloseState);
  }

  async startRoom() {
    this.startLastResortTimeout();

    this.logger.log("Starting room for " + this.rtcId, this);
    this.localPeer = this.state.addPeer("local");

    var signalingPromise = this.startSignaling();

    signalingPromise.then(async () => {
      console.log("Signaling started");
      this.signaling.on("message", (event) => this.processMessage(event));
      this.logger.log("Signaling message handler set up");
      await this.signaling.signalingRegister();
      this.logger.log("signalingRegister complete");
    });

    var joinPromise = this.joinRoom();

    joinPromise.then(async () => {
      this.logger.log("joinRoom complete");
    });

    let disableMedia = this.options.disableVideo && this.options.disableAudio;
    var getUserMediaPromise = disableMedia ? Promise.resolve() : this.getUserMedia();

    await joinPromise;

    var pcCreatePromise = this.createPeerConnection();

    pcCreatePromise.then(async () => {
      this.logger.log("createPeerConnection complete");
      this.addDisconnectionTimeout();
      this.createPeerConnectionEvents();

      this.textChannel = new TextChannel(this);
      this.listenTextChannelMessages();

      await getUserMediaPromise;
      if (!disableMedia) await this.addLocalStream();

      // when not initiator, wait for signaling to be ready because we will send the answer and candidates using signaling
      // but initiator does not wait for signaling to be ready because it uses http to send answer and candidates
      if (!this.isInitiator) await signalingPromise;

      await this.processRoomMessages();

      if (this.isInitiator) {
        await this.sendOffer();

        if (this.state.role == "agent") {
          this.sendAgentConnected();
        }

        this.listenSelectedCandidatePairChanges();
        this.enableRenegotiation = true;
      }

      this.startConnectTimeoutCheck();
    });

    Promise.all([pcCreatePromise, getUserMediaPromise]).then(async () => { });
  }

  enableRenegotiation = false;

  async sendAgentConnected() {
    this.logger.log("Sending AgentConnected");
    try {
      if (!this.state.muteEvents) {
        await axios.get("/api/AgentConnected?rtcId=" + this.rtcId);
      }
    } catch (e) {
      this.logger.error("AgentConnected error");
      this.logger.error(e.toString());
    }
  }

  listenSelectedCandidatePairChanges() {
    try {
      this.peerConnection.sctp.transport.iceTransport.addEventListener("selectedcandidatepairchange", () => {
        let pair = this.peerConnection.sctp.transport.iceTransport.getSelectedCandidatePair();
        this.logger.log("selectedcandidatepairchange", pair);
      });
    } catch (e) {
      this.logger.log("listenSelectedCandidatePairChanges error", e);
    }
  }

  addDisconnectionTimeout() {
    this.peerConnection.addEventListener("connectionstatechange", () => {
      if (this.peerConnection.connectionState === "disconnected") {
        this.callEventEmitter.emit("webrtc-ice-disconnect-popup-show");
        return;
      }

      if (this.peerConnection.connectionState === "connected") {
        this.callEventEmitter.emit("webrtc-ice-disconnect-popup-hide");
        return;
      }

      if (this.peerConnection.connectionState === "failed") {
        if (this.peerConnection.iceConnectionState === "connected" || this.peerConnection.iceConnectionState === "completed") {
          this.hasEstablishedConnection = true;
        }

        if (!this.hasEstablishedConnection) {
          this.logger.log("Peer connection failed without ever being connected, likely ghost call");
          // send event as ghost call
          this.callEventEmitter.emit(Events.CallEvent, { name: "TerminatedOnAgent", data: "peer connection failed" });
        }

        this.localActionEmitter.emit(Actions.HangUp, {
          hangupBy: "webrtc-disconnect",
        });
      }
    });
  }

  startConnectTimeoutCheck() {
    this.connectionTimeout = window.CONFIG.WEBRTC_CONNECTION_TIMEOUT || 10000;

    if (this.peerConnection.connectionState != "connected") {
      this.logger.log("starting  connection timeout check", this.connectionTimeout);
      const halfWay = this.connectionTimeout / 2;

      this.popupTimeoutId = setTimeout(() => {
        this.logger.log("showing connection timeout popup");
        this.callEventEmitter.emit("webrtc-connection-timeout-popup-show");
      }, halfWay);

      this.connectionTimeoutId = setTimeout(() => {
        if (this.peerConnection && this.peerConnection.connectionState === "connected") {
          this.logger.log("Peer connection connected, no need for timeout hangup");
          return;
        }
        this.logger.log("connection timeout reached, hanging up the call");

        if (!this.hasEstablishedConnection) {
          this.logger.log("Connection timeout without ever being connected, likely ghost call");
          // send event as ghost call
          this.callEventEmitter.emit(Events.CallEvent, { name: "TerminatedOnAgent", data: "connection timeout" });
        }

        this.localActionEmitter.emit(Actions.HangUp, {
          hangupBy: "connectionTimeout",
        });
      }, this.connectionTimeout);

      this.callEventEmitter.once(Events.WebRTCConnected, () => {
        this.logger.log("WebRTC connected, stopping connection timeout check");
        this.endConnectTimeoutCheck();
      });
    } else {
      this.logger.log("Peer connection already connected, no need for timeout check");
    }
  }

  startLastResortTimeout() {
    this.lastResortTimeout = 30000; // 30 seconds

    this.logger.log("starting last resort connection timeout check", this.lastResortTimeout);
    const halfWay = this.lastResortTimeout / 2;

    this.lastResortPopupTimeoutId = setTimeout(() => {
      this.logger.log("showing last resort connection timeout popup");
      this.callEventEmitter.emit("webrtc-connection-timeout-popup-show");
    }, halfWay);

    this.lastResortConnectionTimeoutId = setTimeout(() => {
      if (this.peerConnection && this.peerConnection.connectionState === "connected") {
        this.logger.log("Peer connection connected, no need for last resort timeout hangup");
        return;
      }

      this.logger.log("last resort connection timeout reached, hanging up the call");
      this.localActionEmitter.emit(Actions.HangUp, {
        hangupBy: "connectionTimeout",
      });
    }, this.lastResortTimeout);

    this.callEventEmitter.once(Events.WebRTCConnected, () => {
      this.logger.log("WebRTC connected, stopping last resort connection timeout check");
      this.endConnectTimeoutCheck();
    });
  }

  endConnectTimeoutCheck() {
    clearTimeout(this.popupTimeoutId);
    clearTimeout(this.connectionTimeoutId);
    clearTimeout(this.lastResortPopupTimeoutId);
    clearTimeout(this.lastResortConnectionTimeoutId);
    this.callEventEmitter.emit("webrtc-connection-timeout-popup-hide");
  }

  createPeerConnectionEvents() {
    this.peerConnection.onicecandidate = this.sendIceCandidate.bind(this);
    this.peerConnection.ontrack = this.onRemoteTrack.bind(this);

    this.peerConnection.addEventListener("iceconnectionstatechange", this.onIceConnectionStateChange.bind(this));

    this.peerConnection.addEventListener("icegatheringstatechange", this.onIceGatheringStateChange.bind(this));

    this.peerConnection.addEventListener("connectionstatechange", this.onConnectionStateChange.bind(this));

    this.peerConnection.addEventListener("negotiationneeded", this.onNegotiationNeeded.bind(this));

    this.peerConnection.addEventListener("signalingstatechange", this.onSignalingStateChange.bind(this));
  }

  sendCurrentCameraStatus() {
    this.localActionEmitter.emit(Actions.SendTextChannelMessage, {
      message: `CAMERASTATUS|${this.currentCamera}`,
    });
  }

  listenTextChannelMessages() {
    this.callEventEmitter.on(Events.TextChannelMessageReceived, ({ data }) => {
      // FORNOW , ON WEB WE EMULATE AS IF WE ARE REALLY SWAPPING CAMERAS
      // TODO: SWAP CAMERAS ON WEB
      if (data.startsWith("TAKEPHOTO")) {
        const parts = data.split("|");
        const swap = parts[1] == "SWAP";

        if (swap) {
          this.currentCamera = this.currentCamera == "f" ? "b" : "f";
          this.sendCurrentCameraStatus();
        }
      }

      if (data.startsWith("TAKEPHOTO_ON|")) {
        this.state.snackbar.msg = i18n.global.t("PhotoIsBeingCaptured");
        this.state.snackbar.show = true;
      } else if (data.startsWith("TAKEPHOTO_OFF|")) {
        this.state.snackbar.show = false;
      }
    });
  }

  onIceConnectionStateChange() {
    const iceState = this.peerConnection.iceConnectionState;
    this.state.addConnectionLog(`ICE-CONNECTION-` + iceState.toUpperCase());

    if (iceState === "connected" || iceState === "completed") {
      this.logger.log("ICE connected - Network path found.");

      // 1. CLEAR the hard "Connection Timeout" immediately.
      // We don't want to hang up in 10s if we are just waiting for DTLS.
      this.endConnectTimeoutCheck();

      // 2. If we are not fully connected yet (DTLS pending), wait a bit longer
      // before declaring it a "ghost call".
      if (this.peerConnection.connectionState !== "connected") {
        this.logger.log("ICE ok, waiting for DTLS...");

        // Clear existing timeout if a flap occurred
        if (this.dtlsTimeoutId) clearTimeout(this.dtlsTimeoutId);

        // Add a safety fallback: If DTLS doesn't finish in 10 seconds AFTER Ice, then hangup.
        this.dtlsTimeoutId = setTimeout(() => {
          if (this.peerConnection.connectionState !== "connected" && !this.isHangedUp) {
            this.logger.log("ICE connected but DTLS failed (MTU issue? Firewall?). Hanging up.");
            this.localActionEmitter.emit(Actions.HangUp, { hangupBy: "dtlsTimeout" });
          }
        }, 10000);
      }
    }
  }

  onIceGatheringStateChange() {
    this.state.addConnectionLog(`ICE-GATHER-` + this.peerConnection.iceGatheringState.toUpperCase());
  }

  onConnectionStateChange() {
    if (this.peerConnection.connectionState === "connected") {
      this.endConnectTimeoutCheck();
      if (this.dtlsTimeoutId) clearTimeout(this.dtlsTimeoutId);

      if (!this.hasEstablishedConnection) {
        this.hasEstablishedConnection = true;
        this.callEventEmitter.emit(Events.WebRTCConnected);
      }
    }

    this.state.addConnectionLog(`RTC-CONNECTION-` + this.peerConnection.connectionState.toUpperCase());
  }

  onSignalingStateChange() {
    this.state.addConnectionLog(`RTC-SIGNALING-` + this.peerConnection.signalingState.toUpperCase());
  }

  onNegotiationNeeded() {
    this.state.addConnectionLog("ICE-NEGOTIATION-NEEDED");
    if (this.enableRenegotiation) {
      this.sendOffer();
    }
  }

  async addLocalStream() {
    this.logger.log("Adding local stream");

    this.localStream.getTracks().forEach((track) => this.peerConnection.addTrack(track, this.localStream));

    if (this.options?.extraStreams) {
      this.extraStreamClonedTracks = [];
      this.options.extraStreams.forEach((stream) => {
        console.log("Adding extra stream (cloned tracks)", stream);
        stream.getTracks().forEach((track) => {
          const clonedTrack = track.clone();
          this.extraStreamClonedTracks.push(clonedTrack);
          this.peerConnection.addTrack(clonedTrack, stream);
        });
      });
    }

    this.logger.log("Got user media, starting local video");
    this.state.setPeerSrcObject(this.localPeer.peerName, this.localStream);

    this.state.addConnectionLog("ON-LOCAL-STREAM");

    this.callEventEmitter.emit(Events.OnLocalStream, this.localStream);

    if (this.options?.disableAudio) {
      this.roomActionController.muteGeneric("Audio", true, false);
    }

    if (this.options?.disableVideo) {
      this.roomActionController.muteGeneric("Video", true, false);
    }
  }

  streamIdToPeerName = {};

  onRemoteTrack(event) {
    // loop ovr all streams with index
    var stream = event.streams[0];

    if (!Object.keys(this.streamIdToPeerName).includes(stream.id)) {
      console.log("Remote stream id not found in streamIdToPeerName", stream.id);
      console.log("Adding new peer for remote stream");
      var newPeer = this.state.addPeer("remote");
      this.streamIdToPeerName[stream.id] = newPeer.peerName;
      console.log("New peer added for remote stream", newPeer.peerName);
    }
    let peerName = this.streamIdToPeerName[stream.id];
    this.state.setPeerSrcObject(peerName, stream);

    this.callEventEmitter.emit(Events.OnRemoteStream, stream);

    this.state.addConnectionLog("ON-REMOTE-STREAM" + peerName);
  }

  async createPeerConnection() {
    if (typeof RTCPeerConnection.generateCertificate === "function") {
      this.logger.log("Generating certs for RTCPeerConnection");
      var certParams = {
        name: "ECDSA",
        namedCurve: "P-256",
      };
      this.certs = await RTCPeerConnection.generateCertificate(certParams);
    }
    let pc_config = {
      certificates: [this.certs],
      iceServers: this.iceServers,
      bundlePolicy: this.pcConfig.bundlePolicy,
      rtcpMuxPolicy: this.pcConfig.rtcpMuxPolicy,
      iceTransportPolicy: this.pcConfig.iceTransportPolicy || "all",
      iceCandidatePoolSize: this.pcConfig.iceCandidatePoolSize || 0,
    };
    try {
      this.logger.log("Creating peer connection", pc_config);
      this.peerConnection = new RTCPeerConnection(pc_config);

      this.state.addConnectionLog("PEER-CONNECTION-CREATED");

      this.logger.log("Created peer connection", this.peerConnection);
    } catch (e) {
      console.log("Error in creating peer connection ", e);
      this.state.addConnectionLog("ERROR-PEER-CONNECTION-FAILED");
    }
  }

  async processRoomMessages() {
    if (this.messages && this.messages.length > 0) {
      this.logger.log("Processing " + this.messages.length + " messages");
      for (const storedMessage of this.messages) {
        var message = null;
        if (typeof storedMessage === "string") {
          message = JSON.parse(storedMessage);
        } else {
          message = storedMessage;
        }

        this.logger.log("Processing stored signaling message: " + JSON.stringify(message));
        await this.processMessage(message);
      }
    }
  }

  async getUserMedia() {
    try {
      let mediaConstraints;
      let mediaConstraintsReq = axios.post("/media-constraints");

      mediaConstraintsReq.then((response) => {
        localStorage.setItem("media-constraints", JSON.stringify(response.data));
        this.logger.log("Caching media constraints to localStorage", response.data);
      });

      var mediaConstraintsLastTime = localStorage.getItem("media-constraints");
      if (mediaConstraintsLastTime) {
        mediaConstraints = JSON.parse(mediaConstraintsLastTime);
        this.logger.log("Using cached media constraints from localStorage", mediaConstraints);
      } else {
        this.logger.log("No cached media constraints found in localStorage");
        let response = await mediaConstraintsReq;
        mediaConstraints = response.data;
        this.logger.log("Fetched media constraints from server", mediaConstraints);
      }

      if (this.options.disableVideo) {
        mediaConstraints.video = false;
        console.log("WebrtcRoom options disableVideo");
      }

      if (this.options.disableAudio) {
        mediaConstraints.audio = false;
      }

      this.logger.log("mediaConstraints", mediaConstraints);

      this.state.addConnectionLog("GETTING-USER-MEDIA");
      this.logger.log("Getting user media");
      const localStreamRef = await this.deviceState.getUserMedia(mediaConstraints, this.rtcId);
      this.localStream = localStreamRef.value;

      watch(localStreamRef, (newVal) => {
        let cacheOldStream = this.localStream;
        this.localStream = newVal;
        console.log("localStream changed", newVal);
        this.localActionEmitter.emit(Actions.SetMediaStream, {
          media: this.localStream,
        });

        cacheOldStream.getTracks().forEach((track) => {
          var newTrack = newVal.getTracks().find((t) => t.kind === track.kind);

          if (newTrack) {
            newTrack.enabled = track.enabled;
            newTrack.isMutedByRemote = track.isMutedByRemote;
          }

          console.log("Stopping old track", track.kind);
          track.stop();
        });

        this.sendCurrentCameraStatus();
      });

      // log all tracks constraints
      this.localStream.getTracks().forEach((track) => {
        this.logger.log("getConstraints result for " + track.kind, track.getConstraints());
        this.logger.log("getSettings result for " + track.kind, track.getSettings());
      });

      this.state.addConnectionLog("GOT-USER-MEDIA");
      this.callEventEmitter.emit(Events.OnUserMedia);

      this.logger.log("Started local video");
    } catch (error) {
      this.logger.error(error);
      this.state.addConnectionLog("ERROR-GETTING-USER-MEDIA");
      this.state.addConnectionLog(error.name + "|" + (error.message ? error.message : ""));

      const errorMsg =
        userMediaErrorT(error) +
        " (" +
        (error.name || error.toString()) +
        " " +
        (error.message ? ": " + error.message : "") +
        ")";

      Swal.fire({
        icon: "error",
        title: i18n.global.t("UserMediaError"),
        text: errorMsg,
        background: "#363535",
        color: "#ffffff",
        confirmButtonText: i18n.global.t("Ok"),
        confirmButtonColor: "#3085d6",
      });

      setTimeout(() => {
        // end the call after 5 seconds
        this.localActionEmitter.emit(Actions.HangUp, { hangupBy: this.state.role + "MediaError" });
      }, 5000);
    }
  }

  async joinRoom() {
    try {
      this.state.addConnectionLog("JOINING-WEBRTC-ROOM");
      const response = await this.axiosClient.post(
        "join/" +
        this.rtcId +
        "/" +
        this.clientId +
        (this.state.role == "agent" ? "?" + new URLSearchParams({ isAgent: true }).toString() : ""),
      );

      var data = response.data;
      if (data.result !== "SUCCESS") {
        this.errorState = true;
        this.errors.push("ROOM JOIN error: " + data.result);
        this.state.addConnectionLog("JOIN-WEBRTC-ROOM-FAILED");
        return;
      }

      var params = data.params;
      this.clientId = params.client_id;
      this.isInitiator = params.is_initiator === "true";
      this.messages = params.messages;
      this.iceServerUrl = params.ice_server_url;
      this.pcConfig = JSON.parse(params.pc_config);
      this.iceServers = params.turn_server_override;

      this.state.addConnectionLog("JOINED-WEBRTC-ROOM");

      this.callEventEmitter.emit(Events.RoomJoined);
    } catch (e) {
      this.logger.log("joinRoom Error" + e.toString());
      this.state.addConnectionLog("JOIN-WEBRTC-ROOM-FAILED");
      this.errorState = true;
      this.errors.push("joinRoom error: " + e.toString());

      setTimeout(() => {
        // end the call after 5 seconds
        this.localActionEmitter.emit(Actions.HangUp, { hangupBy: "joinRoomError" });
      }, 5000);
    }
  }

  async startSignaling() {
    this.signaling = new Signaling(this);

    this.signaling.on("connecting", () => {
      this.state.addConnectionLog("CONNECTING-SIGNALING");
    });

    this.signaling.on("connected", () => {
      this.state.addConnectionLog("CONNECTED-SIGNALING");
    });

    this.signaling.on("registered", () => {
      this.state.addConnectionLog("REGISTERED-SIGNALING");
    });

    this.signaling.on("disconnected", () => {
      this.state.addConnectionLog("DISCONNECTED-SIGNALING");
    });

    await this.signaling.startSignaling();
  }

  async sendSignalingMessage(message) {
    var msgString = JSON.stringify(message);
    this.logger.log("Sending signaling message: " + msgString);

    if (this.isInitiator) {
      let roomid = this.rtcId;
      let promise = this.axiosClient.post(`message/${roomid}/${this.clientId}`, message, {
        headers: {
          "Content-Type": "text/plain",
        },
      });

      // only wait for offer/answer messages to be sent
      if (message.type === SignalingMessageTypes.Offer || message.type === SignalingMessageTypes.Answer) {
        await promise;
      }
    } else {
      this.signaling.send({
        cmd: "send",
        msg: msgString,
      });
    }
  }

  async sendOffer() {
    this.logger.log("Creating offer");
    let offer = await this.peerConnection.createOffer({
      offerToReceiveAudio: 1,
      offerToReceiveVideo: 1,
      voiceActivityDetection: false,
    });
    this.state.addConnectionLog("CREATED-OFFER");

    this.logger.log("Created offer", offer);
    await this.peerConnection.setLocalDescription(offer);

    this.state.addConnectionLog("SET-LOCAL-DESCRIPTION");

    this.logger.log("Set local description");

    var message = {
      sdp: offer.sdp,
      type: SignalingMessageTypes.Offer,
    };
    this.logger.log("Sending offer message: " + JSON.stringify(message));
    this.state.addConnectionLog("SENDING-OFFER");

    await this.sendSignalingMessage(message);

    this.state.addConnectionLog("SENT-OFFER");

    this.callEventEmitter.emit(Events.OnRTCOfferSent);

    this.offered = true;

    await this.sendStoredCandidates();
  }

  async sendIceCandidate(event) {
    if (!event.candidate) return;

    var candidate = event.candidate;

    if (this.candidateFilteringMode === "RELAY") {
      if (event.candidate.candidate.indexOf("relay") === -1) {
        this.logger.log("Not sending non-relay ice candidate: " + JSON.stringify(candidate));
        return;
      }
    } else if (this.candidateFilteringMode === "HOST") {
      if (event.candidate.candidate.indexOf("host") === -1) {
        this.logger.log("Not sending non-host ice candidate: " + JSON.stringify(candidate));
        return;
      }
    }

    this.logger.log("ICE candidate event: " + JSON.stringify(candidate));

    var message = {
      type: SignalingMessageTypes.Candidate,
      label: candidate.sdpMLineIndex,
      id: candidate.sdpMid,
      candidate: candidate.candidate,
    };

    var storeIntiator = this.isInitiator && !this.offered;
    var storeReceiver = !this.isInitiator && !this.answered;

    if (storeIntiator || storeReceiver) {
      this.logger.log("Storing candidate message: " + JSON.stringify(message));
      this.storedCandidates.push(message);
      return;
    }

    this.logger.log("Sending candidate message: " + JSON.stringify(message));

    await this.sendSignalingMessage(message);
  }

  async sendStoredCandidates() {
    for (const candidate of this.storedCandidates) {
      this.logger.log("Sending stored candidate message: " + JSON.stringify(candidate));
      await this.sendSignalingMessage(candidate);
    }
    this.storedCandidates = [];
  }

  async processMessage(message) {
    if (message.type === SignalingMessageTypes.Offer) {
      await this.handleOffer(message);
    } else if (message.type === SignalingMessageTypes.Answer) {
      await this.handleAnswer(message);
    } else if (message.type === SignalingMessageTypes.Candidate) {
      await this.handleCandidate(message);
    } else if (message.type === SignalingMessageTypes.Bye) {
      this.localActionEmitter.emit(Actions.HangUp, { hangupBy: "remote" });
    } else {
      this.logger.log("Received custom message type from signaling: " + message.type);
    }
  }

  async handleOffer(message) {
    this.state.addConnectionLog("RECEIVED-OFFER");

    this.logger.log("Received offer message: " + JSON.stringify(message));
    let offer = new RTCSessionDescription({
      sdp: message.sdp,
      type: SignalingMessageTypes.Offer,
    });

    await this.peerConnection.setRemoteDescription(offer);

    this.handleStoredIncomingCandidates();

    this.callEventEmitter.emit(Events.OnRTCOfferReceived);

    this.state.addConnectionLog("SET-REMOTE-DESCRIPTION");

    var answer = await this.peerConnection.createAnswer();

    await this.peerConnection.setLocalDescription(answer);

    this.state.addConnectionLog("SET-LOCAL-DESCRIPTION");

    this.state.addConnectionLog("SENDING-ANSWER");

    this.sendAnswer(answer);

    if (!this.isInitiator) {
      // Double check just to be safe, though handleOffer implies !isInitiator
      this.logger.log("Initial offer/answer complete. Enabling renegotiation handler.");
      this.enableRenegotiation = true;
    }
  }

  async sendAnswer(answer) {
    var message = {
      sdp: answer.sdp,
      type: SignalingMessageTypes.Answer,
    };
    this.logger.log("Sending answer message: " + JSON.stringify(message));

    await this.sendSignalingMessage(message);
    this.answered = true;

    this.state.addConnectionLog("SENT-ANSWER");

    this.callEventEmitter.emit(Events.OnRTCAnswerSent);

    await this.sendStoredCandidates();
  }

  async handleAnswer(message) {
    this.state.addConnectionLog(`RECEIVED-ANSWER`);

    this.logger.log("Received answer message: " + JSON.stringify(message));
    var answer = new RTCSessionDescription({
      type: SignalingMessageTypes.Answer,
      sdp: message.sdp,
    });
    await this.peerConnection.setRemoteDescription(answer);

    this.handleStoredIncomingCandidates();

    this.callEventEmitter.emit(Events.OnRTCAnswerReceived);

    this.state.addConnectionLog("SET-REMOTE-DESCRIPTION");
  }

  /**
   * chrome does not support adding candidates if no remoteDescription is present so we store them until we have a remote description
   */
  storedIncomingCandidates = [];

  async handleStoredIncomingCandidates() {
    let arr = this.storedIncomingCandidates;
    this.storedIncomingCandidates = [];
    for (const candidate of arr) {
      await this.handleCandidate(candidate);
    }
  }

  async handleCandidate(message) {
    this.logger.log("Received candidate message: " + JSON.stringify(message));

    if (this.candidateFilteringMode === "RELAY") {
      if (message.candidate.indexOf("relay") === -1) {
        this.logger.log("Not adding incoming non-relay ice candidate: " + JSON.stringify(message));
        return;
      }
    } else if (this.candidateFilteringMode === "HOST") {
      if (message.candidate.indexOf("host") === -1) {
        this.logger.log("Not adding incoming non-host ice candidate: " + JSON.stringify(message));
        return;
      }
    }

    if (!this.peerConnection?.remoteDescription) {
      this.logger.log("Storing incoming candidate message: " + JSON.stringify(message));
      this.storedIncomingCandidates.push(message);
      return;
    }

    var candidate = new RTCIceCandidate({
      sdpMLineIndex: message.label,
      sdpMid: message.id,
      candidate: message.candidate,
    });

    await this.peerConnection.addIceCandidate(candidate);
    this.logger.log("Added ICE candidate");
  }
}

export default WebRTCRoom;
